import React, { useState, useEffect, useContext, useMemo, useRef } from 'react'
import "../css/ApplyLeave.css"
import "../css/commonStyles.css"
import ApiClient from "../ApiClient";
import moment from 'moment';
import ms from "ms";
import CentralizedTable from '../components/CentralizedTable';
import { useSelector } from "react-redux"
import { selectUser } from "../stores/userSlice";
import { selectIsApiLoading } from "../stores/loadingSlice";
import { FormCardSkeleton, ListCardSkeleton } from "../components/PageSkeletonLoaders";
import { AppContext } from "src/stores/ContextProvider";
import { notify } from "../utils/ToastUtils";
import Tooltip from '@mui/material/Tooltip';
import ActionConfirmModal from '../components/ActionConfirmModal';
import UserAvatar from '../components/UserAvatar';
import { FileText, Users, Home, Ban, Trash2, Undo2, CheckCircle2, Pencil, Info, Check, Clock, X } from 'lucide-react';
import { TextField, Pagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';

function ApplyWFH() {
    const topRef = useRef(null);
    const [editWfh, setEditWfh] = useState({});
    const user = useSelector(selectUser)
    const navigate = useNavigate();
    const [flag, setFlag] = useState(false);
    const isApiLoading = useSelector(selectIsApiLoading);
    const { officeHolidays } = useContext(AppContext);
    
    // Form and Balance States
    const currentDate = new Date();
    const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const minMonthDate = moment(firstDay).format('YYYY-MM-DD');
    const maxMonthDate = moment(lastDay).format('YYYY-MM-DD');

    const [minDate, setMinDate] = useState(null)
    const initialState = { startDt: '', endDt: '', reason: '' }
    const [state, setState] = useState(initialState)
    const [totalDays, setTotalDays] = useState(0)
    const [isDisabled, setIsDisabled] = useState(false)
    const [FullOrHalf, setFullOrHalf] = useState(false)
    const [formError, setFormError] = useState("");
    const [daysDisabled, setDaysDisabled] = useState(true);

    const hasError = (field) => {
        if (!formError) return false;
        if (formError === "All fields are required" && !state[field]) return true;
        if (field === 'startDt' && (formError.includes("weekend") || formError.includes("Saturday") || formError.includes("holiday") || formError.includes("lesser than End Date") || formError.includes("current month"))) return true;
        if (field === 'endDt' && (formError.includes("Saturday") || formError.includes("lesser than End Date") || formError.includes("current month"))) return true;
        return false;
    };
    const [page, setPage] = useState(1);
    const itemsPerPage = 5;
    
    // Team Members WFH state
    const [upcomingWfh, setUpcomingWfh] = useState([]);

    // WFH Request Status state
    const [wfhRows, setWfhRows] = useState([]);
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false, actionType: null, rowData: null, title: '', message: '', loading: false
    });

    useEffect(() => {
        if (state.startDt == state.endDt && state.startDt !== "") {
            setDaysDisabled(false)
        } else {
            setFullOrHalf(false);
            setDaysDisabled(true)
        }
    }, [state])

    function handleChange(evt) {
        const { name, value } = evt.target;
        if (name === "startDt" || name === "endDt") {
            if (value) {
                const day = new Date(value).getUTCDay();
                if (day === 0 || day === 6) {
                    setState((prev) => ({ ...prev, [name]: "" }));
                    setFormError("Saturdays and Sundays are not allowed");
                    return; 
                }
            }
        }
        
        setState((prev) => {
            const newState = { ...prev, [name]: value };
            
            setFormError((prevError) => {
                if (prevError === "All fields are required") {
                    if (newState.startDt && newState.endDt && newState.reason) {
                        return "";
                    }
                    return prevError;
                }
                
                if (name === 'startDt' || name === 'endDt') {
                    if (prevError.includes("Saturday") || prevError.includes("weekend") || prevError.includes("lesser than End Date") || prevError.includes("holiday") || prevError.includes("current month")) {
                        return "";
                    }
                } else if (prevError && prevError !== "All fields are required" && !prevError.includes("Saturday") && !prevError.includes("weekend") && !prevError.includes("lesser than End Date") && !prevError.includes("holiday") && !prevError.includes("current month")) {
                    return "";
                }
                return prevError;
            });
            
            return newState;
        });
    }

    const handleFullOrHalf = (e) => {
        if (daysDisabled) {
            notify.warning("Half day WFH is only available for a single day");
            return;
        }
        setFullOrHalf(e.target.checked)
    }

    const initWfhData = (wfh) => {
        setEditWfh(wfh);
        let wfhInitial = {
            startDt: moment(wfh.START_DATE).format('YYYY-MM-DD'),
            endDt: moment(wfh.END_DATE).format('YYYY-MM-DD'),
            reason: wfh.REASON
        }
        if (wfh?.NO_OF_DAYS == 0.5) setFullOrHalf(true)
        setState(wfhInitial)
        if (topRef.current) {
            topRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }

    const initDateRange = () => {
        if (state.startDt) {
            const minsec = ms('0d')
            const min_date = new Date(new Date(state.startDt) - minsec);
            let calculatedMinDate = moment(min_date).format('YYYY-MM-DD');
            if (calculatedMinDate < minMonthDate) {
                calculatedMinDate = minMonthDate;
            }
            setMinDate(calculatedMinDate);
        } else {
            setMinDate(minMonthDate);
        }
    }

    const isInArray = (value) => {
        return (officeHolidays.find(item => { return item === value }) || []).length > 0;
    }

    const findNoOfDays = (sDate, eDate) => {
        let startDate = new Date(sDate);
        let endDate = new Date(eDate);
        startDate = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth(), startDate.getUTCDate()));
        endDate = new Date(Date.UTC(endDate.getUTCFullYear(), endDate.getUTCMonth(), endDate.getUTCDate()));
        endDate.setUTCDate(endDate.getUTCDate() + 1);
        let daysCount = 0;
        while (startDate < endDate) {
            const day = startDate.getUTCDay(); 
            if (day !== 0 && day !== 6) {
                if (!isInArray(moment.utc(startDate).format("YYYY-MM-DD"))) {
                    daysCount++;
                }
            }
            startDate.setUTCDate(startDate.getUTCDate() + 1);
        }
        return daysCount;
    };

    const sendWfhRequest = async () => {
        setIsDisabled(true)
        setFormError("");
        if (state.startDt && state.endDt && state.reason) {
            if (new Date(state.startDt) <= new Date(state.endDt)) {
                
                // Restrict to current month only
                const startDateObj = new Date(state.startDt);
                const endDateObj = new Date(state.endDt);
                if (startDateObj.getMonth() !== currentDate.getMonth() || startDateObj.getFullYear() !== currentDate.getFullYear() || endDateObj.getMonth() !== currentDate.getMonth() || endDateObj.getFullYear() !== currentDate.getFullYear()) {
                    setFormError("WFH can only be applied for the current month");
                    setIsDisabled(false);
                    return;
                }

                let noOfDays = "";
                if (state.startDt === state.endDt) {
                    const day = new Date(state.startDt).getUTCDay(); 
                    if (day === 0 || day === 6) {
                        setFormError("Selected date falls on a weekend (Sat/Sun)");
                        setIsDisabled(false);
                        return;
                    }
                    if (isInArray(state.startDt)) {
                        setFormError("Selected date is an office holiday");
                        setIsDisabled(false);
                        return;
                    }
                    noOfDays = FullOrHalf ? 0.5 : 1;
                } else {
                    noOfDays = findNoOfDays(state.startDt, state.endDt);                
                }
                
                try {
                    await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/leave/applyWfh`, {
                        code: user.emp_code,
                        name: user.name,
                        startDt: state.startDt,
                        endDt: state.endDt,
                        noOfDays: noOfDays,
                        reason: state.reason,
                        status: (totalDays === 0 || totalDays < noOfDays) ? "Pending" : "Accepted",
                        requestType: editWfh.REQUEST_ID ? "edit" : "",
                        requestId: editWfh.REQUEST_ID
                    }, { headers: { skipLoader: true } })
                    notify.success(editWfh.REQUEST_ID ? "WFH Request updated successfully" : "WFH Request sent successfully")
                    setState(initialState)
                    setEditWfh({})
                    setIsDisabled(false)
                    fetchWfhRequestsStatus();
                    fetchWfhBalance();
                    fetchUpcomingWfhTeam();
                } catch (error) {
                    console.log(error)
                    let errmsg = (error?.response) ? (error?.response?.data?.message ? error?.response?.data?.message : `${error?.response?.status} - ${error?.response?.statusText}`) : "Something went wrong. Please try again later"
                    setFormError(errmsg)
                    setIsDisabled(false)
                }
            } else {
                setFormError("Start Date should be lesser than End Date");
                setIsDisabled(false)
            }
        } else {
            setFormError("All fields are required")
            setIsDisabled(false)
        }
    }

    const fetchWfhBalance = async () => {
        try {
            const { data } = await ApiClient.get(`${process.env.REACT_APP_BASE_URL}/leave/WfhBalance`)
            setTotalDays(data[0]?.WFH_BALANCE ?? 0);
        } catch (error) {
            console.log(error)
        }
    }

    const fetchUpcomingWfhTeam = async()=>{
        try {
            const { data } = await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/leave/upcomingWfh`, { empcode: user?.emp_code })
            setUpcomingWfh(data)
        } catch (error) {
            console.log(error);
        }
    }

    // WFH Request Status Logic
    const openConfirmModal = (actionType, rowData, title, message) => {
        setConfirmModal({ isOpen: true, actionType, rowData, title, message, loading: false });
    };

    const closeConfirmModal = () => {
        setConfirmModal({ isOpen: false, actionType: null, rowData: null, title: '', message: '', loading: false });
    };

    const handleConfirm = async () => {
        const { actionType, rowData } = confirmModal;
        setConfirmModal(prev => ({ ...prev, loading: true }));
        if (actionType === 'deleteWfh') {
            await handleWFHDelete(rowData);
        } else if (actionType === 'undoWfh') {
            await handelDeleteWFHUndo(rowData);
        }
        closeConfirmModal();
    };

    const wfhColumns = useMemo(() => [{
        headerName: "Start Date", field: "START_DATE", sortable: true, width: 120,
        valueGetter: (params) => moment(params.row?.START_DATE).format("DD-MMM-YYYY")
    }, {
        headerName: "End Date", field: "END_DATE", sortable: true, width: 120,
        valueGetter: (params) => moment(params.row?.END_DATE).format("DD-MMM-YYYY")
    }, {
        headerName: "#Days", field: "NO_OF_DAYS", sortable: true, width: 80,
        valueGetter: (params) => Number(params.row?.NO_OF_DAYS) || params.row?.NO_OF_DAYS
    }, {
        headerName: "Reason", field: "REASON", sortable: true, width: 350,
        renderCell: (params) => (
            <Tooltip title={params?.row?.REASON || ""} arrow placement="top">
                <div className="truncate block" style={{ maxWidth: '310px' }}>
                    {params?.row?.REASON}
                </div>
            </Tooltip>
        )
    }, {
        headerName: "Status", field: "STATUS", sortable: true, width: 120,
        renderCell: (params) => {
            const status = params?.row?.STATUS || "Pending";
            const colorClass = status === "Accepted" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : status === "Pending" ? "bg-amber-100 text-amber-700 border-amber-200" : "bg-rose-100 text-rose-700 border-rose-200";
            return <div className="flex items-center h-full"><span className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${colorClass}`}>{status}</span></div>;
        }
    },{
        headerName: "Action", field: "Action", sortable: false, width: 100, filterable: false,
        renderCell: (params) => {
            return <div className="flex items-center h-full">
                {/* Fixed space for Edit */}
                <div className="w-8 flex justify-center">
                {params.row.STATUS !== "Accepted" && (
                    <Tooltip title="Edit">
                    <span onClick={() => initWfhData(params.row)}>
                        <Pencil
                        size={16}
                        color="#2563eb"
                        className="cursor-pointer hover:scale-110 transition-transform"
                        />
                    </span>
                    </Tooltip>
                )}
                </div>

                {/* Fixed space for Delete / Undo */}
                <div className="w-8 flex justify-center">
                {moment(params.row.START_DATE).isBefore(moment().startOf('month')) ? (
                    <Tooltip title="Cannot modify previous month's WFH">
                        <span className="cursor-not-allowed inline-flex">
                            <Trash2 size={16} className="text-gray-300" />
                        </span>
                    </Tooltip>
                ) : params.row.REQUEST_TYPE !== 1 ? (
                    params.row.DELETE_STATUS === "Undo" || params.row.DELETE_STATUS === "Reject" ? (
                        <span className="cursor-not-allowed inline-flex">
                            <Trash2 size={16} className="text-gray-300" />
                        </span>
                    ) : (
                        <Tooltip title="Delete">
                            <span onClick={() => {
                                const start = moment(params.row.START_DATE).format("DD-MMM-YYYY");
                                const end = moment(params.row.END_DATE).format("DD-MMM-YYYY");
                                openConfirmModal('deleteWfh', params.row, 'Confirm Deletion', `Are you sure you want to delete this WFH request (${start} to ${end})?\nThis action cannot be undone.`);
                            }}>
                                <Trash2 size={16} color="rgb(236, 59, 59)" style={{ cursor: "pointer" }} />
                            </span>
                        </Tooltip>
                    )
                ) : (
                    <Tooltip title="Undo">
                        <span onClick={() => {
                            const start = moment(params.row.START_DATE).format("DD-MMM-YYYY");
                            const end = moment(params.row.END_DATE).format("DD-MMM-YYYY");
                            openConfirmModal('undoWfh', params.row, 'Confirm Revert', `Are you sure you want to revert the cancellation of this WFH request (${start} to ${end})?\nThis action cannot be undone.`);
                        }}>
                            <Undo2 size={16} color="rgb(79 159 207)" style={{ cursor: "pointer" }} />
                        </span>
                    </Tooltip>
                )}
                </div>
            </div>
        }
    }], []);

    const fetchWfhRequestsStatus = async () => {
        try {
            const { data } = await ApiClient.post(`${process.env.REACT_APP_BASE_URL}/leave/prevWFHRequests`, { empcode: user?.emp_code })
            setWfhRows(data)
        } catch (error) {
            console.log(error)
        }
    }

    const handleWFHDelete = async (row) => {
        if (new Date(row.START_DATE) > new Date()) {
            try {
                await ApiClient.delete(`${process.env.REACT_APP_BASE_URL}/leave/deleteWfh`, { data: { data: row }, headers: { skipLoader: true } })
                notify.success("WFH Deleted Successfully");
                fetchWfhRequestsStatus();
                fetchWfhBalance();
                fetchUpcomingWfhTeam();
            } catch (error) {
                notify.error("Something went wrong. Please try again later")
            }
        } else {
            try {
                await ApiClient.put(`${process.env.REACT_APP_BASE_URL}/leave/rejectDeleteWfh`, { REQUEST_ID: row.REQUEST_ID, STATUS: "Request" }, { headers: { skipLoader: true } })
                notify.success("Submitted For Manager Approval");
                fetchWfhRequestsStatus();
                fetchWfhBalance();
                fetchUpcomingWfhTeam();
            } catch (error) {
                notify.error("Something went wrong. Please try again later")
            }
        }
    }

    const handelDeleteWFHUndo = async (row) => {
        try {
            await ApiClient.put(`${process.env.REACT_APP_BASE_URL}/leave/rejectDeleteWfh`, { REQUEST_ID: row.REQUEST_ID, STATUS: "Undo" }, { headers: { skipLoader: true } })
            notify.success("WFH cancellation request revoked");
            fetchWfhRequestsStatus();
            fetchWfhBalance();
            fetchUpcomingWfhTeam();
        } catch (error) {
            notify.error("Something went wrong. Please try again later")
        }
    }

    useEffect(()=>{
        fetchWfhBalance();
        fetchUpcomingWfhTeam();
        fetchWfhRequestsStatus();
    },[])

    useEffect(() => {
        initDateRange();
    }, [state.startDt]);

    return (
        <div className="flex flex-col gap-3" ref={topRef}>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-3">
                {/* Apply WFH Form Card */}
                {isApiLoading ? <FormCardSkeleton className="lg:col-span-3" /> : (
                <div className="lg:col-span-3 bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col relative overflow-hidden group">
                    <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-4">
                        <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-9 h-9 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
                                <Home className="w-5 h-5" />
                            </div>
                            <div>
                                <h3 className="text-[15px] font-bold text-slate-800 m-0 leading-tight">{editWfh.REQUEST_ID ? "Edit WFH Request" : "Apply WFH"}</h3>
                                <p className="text-[13px] text-slate-500 m-0 hidden sm:block mt-1">Fill in the details below to submit your work from home request</p>
                            </div>
                        </div>
                        <button 
                            onClick={() => navigate('/policies')}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-brand-600 bg-white border border-brand-100 rounded-lg hover:bg-brand-50 transition-colors shadow-sm shrink-0 whitespace-nowrap">
                            <FileText className="w-4 h-4 shrink-0" /> <span className="hidden sm:inline">WFH Policy</span><span className="sm:hidden">Policy</span>
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">Start Date <span className="text-red-500">*</span></label>
                            <TextField type="date" name='startDt' value={state.startDt} onChange={handleChange} inputProps={{ min: minMonthDate, max: maxMonthDate }} size="small" fullWidth error={hasError('startDt')} className={hasError('startDt') ? '[&_fieldset]:!border-red-500' : ''} sx={{ backgroundColor: '#f8fafc', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' } }} required />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-slate-700 mb-1.5">End Date <span className="text-red-500">*</span></label>
                            <TextField type="date" name='endDt' value={state.endDt} onChange={handleChange} inputProps={{ min: minDate, max: maxMonthDate }} size="small" fullWidth error={hasError('endDt')} className={hasError('endDt') ? '[&_fieldset]:!border-red-500' : ''} sx={{ backgroundColor: '#f8fafc', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' } }} required />
                        </div>
                    </div>

                    <div className="mb-3 flex items-center gap-3">
                        <label className="relative inline-flex items-center cursor-pointer">
                            <input type="checkbox" className="sr-only peer" checked={FullOrHalf} onChange={handleFullOrHalf} />
                            <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-500"></div>
                        </label>
                        <span className="text-sm font-semibold text-slate-700">Yes, I'm applying for half day WFH</span>
                    </div>

                    <div className="mb-3">
                        <label className="block text-sm font-semibold text-slate-700 mb-1.5">Reason for WFH <span className="text-red-500">*</span></label>
                        <TextField multiline rows={3} name="reason" value={state.reason} onChange={handleChange} placeholder="Enter reason for work from home..." size="small" fullWidth error={hasError('reason')} className={hasError('reason') ? '[&_fieldset]:!border-red-500' : ''} sx={{ backgroundColor: '#f8fafc', '& .MuiOutlinedInput-notchedOutline': { borderColor: '#cbd5e1' }, '& textarea': { resize: 'none', overflowY: 'auto !important' } }} required />
                    </div>

                    {formError && (
                        <div className="mb-3 p-2 bg-red-50 border border-red-200 text-red-600 text-sm font-medium rounded-lg flex items-center gap-2">
                            <Info className="w-4 h-4 shrink-0" />
                            {formError}
                        </div>
                    )}

                    <div className="flex items-center justify-between mt-auto pt-4 border-t border-slate-200">
                        <div className="text-sm font-medium text-slate-600">
                            No of days left this month: <span className="text-brand-600 font-bold">{Number(totalDays)}</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {editWfh.REQUEST_ID && (
                                <button type="button" onClick={() => { setEditWfh({}); setState(initialState); }} className="flex items-center gap-2 px-4 py-1.5 bg-white text-slate-700 text-sm rounded-lg hover:bg-slate-50 transition-all font-medium shadow-sm border border-slate-200">
                                    Cancel
                                </button>
                            )}
                            <button type="button" onClick={sendWfhRequest} disabled={isDisabled} className="flex items-center gap-2 px-4 py-1.5 bg-brand-600 text-white text-sm rounded-lg hover:bg-brand-700 transition-all font-medium shadow-sm hover:shadow focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed">
                                {isDisabled && <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>}
                                <span>{isDisabled ? "Loading..." : (editWfh.REQUEST_ID ? "Update WFH" : "Request WFH")}</span>
                            </button>
                        </div>
                    </div>
                </div>
                )}

                {/* Balance & Team Card */}
                {isApiLoading ? <ListCardSkeleton className="lg:col-span-2" /> : (
                <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col">
                    <div className="flex justify-between items-center mb-4 border-b border-slate-100 pb-4">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                                <Users className="w-5 h-5" />
                            </div>
                            <div className="flex flex-col">
                                <h3 className="text-[15px] font-bold text-slate-800 m-0">On WFH / Upcoming</h3>
                                <p className="text-[12px] font-medium text-slate-500 m-0 mt-0.5">
                                    View your team's upcoming WFH
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col gap-1 overflow-y-auto pr-2 custom-scrollbar flex-1" style={{ maxHeight: "400px" }}>
                        {upcomingWfh.length > 0 ? upcomingWfh.slice((page - 1) * itemsPerPage, page * itemsPerPage).map((member, idx) => (
                            <div key={idx} className="flex items-center justify-between py-3 px-2 border-b border-slate-100 hover:bg-slate-50 transition-colors gap-2">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="shrink-0">
                                        <UserAvatar name={member.EMP_NAME} imageUrl={member.img_dir ? `${process.env.REACT_APP_PUBLIC_URL}/${member.img_dir}` : null} size={36} />
                                    </div>
                                    <div className="min-w-0">
                                        <Tooltip title={member.EMP_NAME} arrow placement="top">
                                            <h4 className="text-[13px] sm:text-[14px] font-bold text-slate-800 m-0 mb-0.5 truncate">{member.EMP_NAME}</h4>
                                        </Tooltip>
                                        <Tooltip title={
                                        member.START_DATE === member.END_DATE 
                                            ? moment(member.START_DATE).format("DD MMM, YYYY")
                                            : `${moment(member.START_DATE).format("DD MMM")} - ${moment(member.END_DATE).format("DD MMM, YYYY")}`
                                        } arrow placement="top">
                                        <div className="text-[11px] sm:text-[12px] font-medium text-slate-500 truncate mt-0.5">
                                        {member.START_DATE === member.END_DATE 
                                            ? moment(member.START_DATE).format("DD MMM, YYYY")
                                            : `${moment(member.START_DATE).format("DD MMM")} - ${moment(member.END_DATE).format("DD MMM, YYYY")}`
                                        }
                                    </div>
                                    </Tooltip>
                                    </div>
                                </div>
                                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-1.5 sm:gap-4 text-right sm:text-left shrink-0">
                                    <div className={`whitespace-nowrap shrink-0 px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-bold sm:w-[75px] text-center ${
                                        Number(member.NO_OF_DAYS) === 0.5 
                                            ? 'bg-emerald-50 text-emerald-600 border border-emerald-100'
                                            : 'bg-blue-50 text-blue-600 border border-blue-100'
                                    }`}>
                                        {Number(member.NO_OF_DAYS) === 0.5 ? "Half Day" : `${Number(member.NO_OF_DAYS)} Day${Number(member.NO_OF_DAYS) > 1 ? 's' : ''}`}
                                    </div>
                                    
                                    {user?.is_admin === 'Yes' && (
                                        <div className={`whitespace-nowrap flex items-center justify-center gap-1.5 shrink-0 px-2.5 py-0.5 sm:py-1 rounded-full text-[10px] sm:text-[11px] font-bold border sm:w-[100px] ${
                                            (member.STATUS || member.LEAVE_STATUS)?.toLowerCase() === 'approved' || (member.STATUS || member.LEAVE_STATUS)?.toLowerCase() === 'accepted' ? 'bg-green-50 text-green-700 border-green-200' :
                                            (member.STATUS || member.LEAVE_STATUS)?.toLowerCase() === 'rejected' ? 'bg-red-50 text-red-700 border-red-200' :
                                            'bg-orange-50 text-orange-600 border-orange-200'
                                        }`}>
                                            {((member.STATUS || member.LEAVE_STATUS)?.toLowerCase() === 'approved' || (member.STATUS || member.LEAVE_STATUS)?.toLowerCase() === 'accepted') ? (
                                                <Check className="w-3 h-3" />
                                            ) : (member.STATUS || member.LEAVE_STATUS)?.toLowerCase() === 'rejected' ? (
                                                <X className="w-3 h-3" />
                                            ) : (
                                                <Clock className="w-3 h-3" />
                                            )}
                                            {(member.STATUS || member.LEAVE_STATUS) === 'Approved' ? 'Accepted' : (member.STATUS || member.LEAVE_STATUS) || 'Pending'}
                                        </div>
                                    )}
                                </div>
                            </div>
                        )) : (
                            <div className="h-full flex flex-col sm:flex-row items-center justify-between p-4 bg-slate-50/50 rounded-2xl border border-dashed border-slate-200 mt-2">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                                        <CheckCircle2 className="w-6 h-6 text-emerald-600" strokeWidth={2.5} />
                                    </div>
                                    <div>
                                        <p className="text-[14px] font-bold text-slate-800">You're all caught up!</p>
                                        <p className="text-xs font-medium text-slate-500 mt-0.5">No team members on WFH.</p>
                                    </div>
                                </div>
                                <div className="hidden sm:flex items-center justify-center pr-4 opacity-60">
                                    <div className="relative">
                                        <Users className="w-10 h-10 text-emerald-400 stroke-[1.5]" />
                                        <div className="absolute top-0 right-0 w-3 h-3 bg-emerald-500 rounded-full border-2 border-white flex items-center justify-center">
                                            <CheckCircle2 className="w-1.5 h-1.5 text-white" strokeWidth={4} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    {upcomingWfh.length > itemsPerPage && (
                        <div className="flex justify-center mt-3 shrink-0">
                            <Pagination 
                                count={Math.ceil(upcomingWfh.length / itemsPerPage)} 
                                page={page} 
                                onChange={(e, val) => setPage(val)} 
                                size="small" 
                                sx={{
                                    '& .MuiPaginationItem-root.Mui-selected': {
                                        backgroundColor: '#eff6ff', // tailwind blue-50
                                        color: '#2563eb', // tailwind blue-600
                                        fontWeight: 'bold',
                                        '&:hover': {
                                            backgroundColor: '#dbeafe', // tailwind blue-100
                                        }
                                    }
                                }} 
                            />
                        </div>
                    )}
                </div>
                )}
            </div>

            {/* WFH Request Status Card */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="p-0 pt-1">
                    <CentralizedTable
                        getRowId={row => row.REQUEST_ID}
                        rows={wfhRows}
                        columns={wfhColumns}
                        isLoading={isApiLoading}
                        autoHeight
                        renderTopToolbarCustomActions={() => (
                            <h3 className="text-[15px] font-bold text-slate-800 m-0">WFH Request Status</h3>
                        )}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 5, page: 0 } },
                            columnPinning: { right: ['Action'] }
                        }}
                        enableColumnPinning={true}
                        muiTableHeadCellProps={({ column }) => ({
                            sx: {
                                boxShadow: 'none !important',
                                '&::before': {
                                    boxShadow: column.id === 'Action' ? '-4px 0 10px rgba(0,0,0,0.05) !important' : 'none !important',
                                }
                            }
                        })}
                        muiTableBodyCellProps={({ cell }) => ({
                            sx: {
                                py: "8px",
                                borderBottom: "1px solid #e2e8f0",
                                px: cell.column.getIndex() === 0 ? '16px' : "3px",
                                overflow: cell.column.id === 'Action' ? 'visible' : 'hidden',
                                textOverflow: cell.column.id === 'Action' ? 'clip' : 'ellipsis',
                                backgroundColor: cell.column.id === 'Action' ? '#fff !important' : 'inherit',
                                boxShadow: 'none !important',
                                '&::before': {
                                    boxShadow: cell.column.id === 'Action' ? '-4px 0 10px rgba(0,0,0,0.05) !important' : 'none !important',
                                }
                            },
                        })}
                        pageSizeOptions={[5, 10,15]}
                        muiTableContainerProps={{
                            sx: {
                                minHeight: "auto",
                                height: 'auto',
                                flex: 1,
                                overflow: 'auto',
                            },
                        }}
                        hideFooterSelectedRowCount
                    />
                </div>
            </div>

            <ActionConfirmModal 
                isOpen={confirmModal.isOpen} 
                onClose={closeConfirmModal} 
                onConfirm={handleConfirm} 
                title={confirmModal.title} 
                message={confirmModal.message} 
                loading={confirmModal.loading} 
            />
        </div>
    )
}

export default ApplyWFH
